/**
 * One clock for every relative time on screen ("5 minutes ago"). Each `Time`
 * used to compute `Date.now()` whenever its own component happened to
 * render, so two timestamps for the same moment on one page could read
 * "5 minutes ago" and "8 minutes ago" (third live review, 2026-09-27). The
 * provider ticks every 30 s and every `Time` reads the same `now`, so they
 * all move together. Without a provider (unit tests) `now` is the render time.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

const ClockContext = createContext<number | null>(null);

export const CLOCK_TICK_MS = 30_000;

export function ClockProvider({ children, tickMs = CLOCK_TICK_MS }: { children: ReactNode; tickMs?: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), tickMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tickMs]);
  return <ClockContext.Provider value={now}>{children}</ClockContext.Provider>;
}

/** The shared "now" in ms. */
export function useNow(): number {
  return useContext(ClockContext) ?? Date.now();
}
